import React, { useState } from 'react';
import {
  TrendingUp, BarChart2, Brain, ScanLine,
  Calculator, Target, ChevronDown,
} from 'lucide-react';

// ─── Instrument tabs ──────────────────────────────────────────────────────────
const INSTRUMENTS = [
  { id: 'equity',  label: 'Intraday Equity', color: '#6c63ff' },
  { id: 'futures', label: 'Futures',         color: '#4ecca3' },
  { id: 'options', label: 'Options Buy',     color: '#f59e0b' },
];

// ─── Steps — 1–4 universal, 5–6 instrument-aware ─────────────────────────────
const STEPS = [
  {
    zone: 'Pre-Market · 8:00 AM', color: '#4ecca3',
    icon: TrendingUp,
    title: 'Check Global Cues',
    desc: 'Before market opens, see where global indices closed overnight. This sets your directional bias for the day.',
    path: '/global-cues',
    type: 'rows',
    rows: [
      { label: 'SGX Nifty',   val: '+47 pts',  sub: '24,785 · +0.21% · bullish cue',        up: true  },
      { label: 'S&P 500 Fut', val: '+0.28%',   sub: '5,892 · US closed positive overnight',  up: true  },
      { label: 'Nikkei 225',  val: '+0.83%',   sub: '38,420 · Asia strongly up',             up: true  },
      { label: 'Crude WTI',   val: '−0.41%',   sub: '$82.4 · slight dip, not alarming',      up: false },
      { label: 'USD / INR',   val: '₹83.42',   sub: '−0.1% · rupee stable',                  up: true  },
    ],
    verdict: '4 of 5 cues positive → Mild bullish bias today',
    verdictColor: '#16C784',
  },
  {
    zone: 'Pre-Market · 8:30 AM', color: '#4ecca3',
    icon: BarChart2,
    title: 'Read FII / DII Flow',
    desc: 'Institutional money tells you conviction. DIIs absorbing FII selling = dip support. Both selling = avoid longs.',
    path: '/global-cues',
    type: 'rows',
    rows: [
      { label: 'FII Cash',        val: '−₹842 Cr',   sub: 'Net sellers in cash market',         up: false },
      { label: 'FII Derivatives', val: '+₹2,340 Cr', sub: 'Net long in F&O · hedged position',  up: true  },
      { label: 'DII Cash',        val: '+₹1,205 Cr', sub: 'Net buyers · absorbing FII selling', up: true  },
      { label: 'Net Cash Flow',   val: '+₹363 Cr',   sub: 'DII buying > FII selling today',     up: true  },
    ],
    verdict: 'DIIs absorbing FII selling → dip support likely. Avoid shorts.',
    verdictColor: '#f59e0b',
  },
  {
    zone: 'Market Open · 9:15 AM', color: '#4ecca3',
    icon: Brain,
    title: 'Read the Regime',
    desc: 'Gap size + first candle defines the day type. This changes your entire playbook. Never skip this step.',
    path: '/market-analysis',
    type: 'rows',
    rows: [
      { label: 'Nifty Prev Close',   val: '24,738',     sub: 'Yesterday 3:30 PM close',            up: null  },
      { label: 'Nifty Open',         val: '24,786',     sub: '+48 pts from prev close',            up: true  },
      { label: 'Gap %',              val: '+0.19%',     sub: 'Small gap up (< 0.5%) → trend day', up: true  },
      { label: 'First 5-min candle', val: 'Green close',sub: 'H 24,810 · L 24,770 · C 24,803',   up: true  },
      { label: 'VWAP at 9:20 AM',    val: '24,792',     sub: 'Price above VWAP ✓ bullish',        up: true  },
    ],
    verdict: 'Small gap up + green 1st candle = Mild Trend Day → ride breakouts, don\'t fade',
    verdictColor: '#16C784',
  },
  {
    zone: 'Stock Selection · 9:20 AM', color: '#4ecca3',
    icon: ScanLine,
    title: 'Find Your Setup',
    desc: 'Use scanners to find a stock at a known resistance level with high volume. The chart tells the story.',
    path: '/analytics',
    type: 'chart',
    verdict: 'Grade A breakout — resistance tested 3 times, now broken with 2.1× volume. SL below base candle.',
    verdictColor: '#16C784',
  },
  {
    zone: 'Risk Sizing · Before Entry',
    colorByInstrument: { equity: '#6c63ff', futures: '#4ecca3', options: '#f59e0b' },
    icon: Calculator,
    title: 'Size Your Position',
    desc: 'Risk a fixed rupee amount — not a fixed lot size. Your capital stays safe even when the trade fails.',
    path: '/calculator',
    type: 'sizing',
    verdictByInstrument: {
      equity:  'Max loss ₹494 · Max gain ₹988 · R:R = 1:2 → only take if ratio is ≥ 1:1.5',
      futures: 'Margin ₹1,15,200 blocked · Max loss ₹8,800/lot · R:R 1:1.8 → meets minimum threshold',
      options: '1 lot cost ₹11,200 · SL exit caps loss at ₹4,400 · R:R 1:2 → valid setup',
    },
    verdictColorByInstrument: { equity: '#6c63ff', futures: '#4ecca3', options: '#f59e0b' },
  },
  {
    zone: 'Entry · 9:30 AM',
    colorByInstrument: { equity: '#f59e0b', futures: '#4ecca3', options: '#f59e0b' },
    icon: Target,
    title: 'Place the Trade',
    desc: 'You have done all the work. Enter with a written reason. No written reason = do not place the trade.',
    path: '/trade',
    type: 'ticket',
    verdictByInstrument: {
      equity:  'Trade placed. Plan is live. Do not move SL. Do not exit early. Let the trade play out.',
      futures: 'Futures position live. Margin blocked. Hard SL at ₹1,418. Do not hold overnight.',
      options: 'CE position live. Exit rule: if premium drops to ₹17 — exit. Do not average down.',
    },
    verdictColorByInstrument: { equity: '#f59e0b', futures: '#16C784', options: '#f59e0b' },
  },
];

// ─── Breakout candlestick chart ───────────────────────────────────────────────
function BreakoutChart() {
  const CW = 280, CH = 98, VH = 20, GV = 4;
  const MIN_P = 1380, MAX_P = 1458, RANGE = MAX_P - MIN_P;
  const py = p => CH - ((p - MIN_P) / RANGE) * CH;
  const RESISTANCE = 1435, SL_LEVEL = 1418;
  const CANDLES = [
    { o: 1394, h: 1410, l: 1388, c: 1405 },
    { o: 1405, h: 1418, l: 1398, c: 1412 },
    { o: 1412, h: 1436, l: 1405, c: 1428 },
    { o: 1428, h: 1435, l: 1418, c: 1422 },
    { o: 1422, h: 1430, l: 1414, c: 1426 },
    { o: 1426, h: 1432, l: 1416, c: 1420 },
    { o: 1420, h: 1435, l: 1412, c: 1417 },
    { o: 1417, h: 1450, l: 1415, c: 1443 },
  ];
  const VOLS = [55, 60, 75, 50, 52, 48, 55, 100];
  const CW_C = 22, CG = 9, N = CANDLES.length;
  const TOTAL_W = N * CW_C + (N - 1) * CG;
  const SX = Math.floor((CW - TOTAL_W) / 2);
  const cx = i => SX + i * (CW_C + CG) + CW_C / 2;
  const bx = i => SX + i * (CW_C + CG);
  const rY = py(RESISTANCE), slY = py(SL_LEVEL);

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', background: '#080912', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px 3px' }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#eceef5' }}>INFY · Daily</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <span style={{ fontSize: 8, color: '#ef4444', opacity: 0.85 }}>— ₹1,435 resistance</span>
          <span style={{ fontSize: 8, fontWeight: 700, color: '#16C784' }}>↑ breakout</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${CW} ${CH + GV + VH}`} style={{ width: '100%', display: 'block' }}>
        <rect x={0} y={rY - 1} width={CW} height={3} fill="#ef4444" opacity="0.06" />
        <line x1={0} y1={rY} x2={CW} y2={rY} stroke="#ef4444" strokeWidth="1" strokeDasharray="4 3" opacity="0.6" />
        <line x1={bx(N-1)-6} y1={slY} x2={CW-2} y2={slY} stroke="#f59e0b" strokeWidth="0.8" strokeDasharray="3 3" opacity="0.55" />
        <text x={bx(N-1)-6} y={slY-3} fontSize="6.5" fill="#f59e0b" opacity="0.7">SL ₹1,418</text>
        {CANDLES.map((c, i) => {
          const isGreen = c.c >= c.o, isBreakout = i === N - 1;
          const col = isGreen ? '#16C784' : '#ef4444';
          const bodyTop = py(Math.max(c.o, c.c)), bodyBot = py(Math.min(c.o, c.c));
          const bodyH = Math.max(bodyBot - bodyTop, 1.5);
          return (
            <g key={i}>
              <line x1={cx(i)} y1={py(c.h)} x2={cx(i)} y2={py(c.l)} stroke={col} strokeWidth={isBreakout ? 1.5 : 1} opacity={isBreakout ? 1 : 0.6} />
              <rect x={bx(i)} y={bodyTop} width={CW_C} height={bodyH} fill={col} opacity={isBreakout ? 1 : 0.5} rx={2} />
              {isBreakout && <rect x={bx(i)-3} y={bodyTop-3} width={CW_C+6} height={bodyH+6} fill="none" stroke="#16C784" strokeWidth="1.5" opacity="0.35" rx={4} />}
            </g>
          );
        })}
        <text x={cx(N-1)-10} y={py(CANDLES[N-1].h)-5} fontSize="8" fontWeight="900" fill="#16C784" opacity="0.95">↑ BRK</text>
        {VOLS.map((v, i) => {
          const isBreakout = i === N - 1;
          const barH = Math.max((v / 100) * VH, 2);
          return <rect key={`v${i}`} x={bx(i)} y={CH+GV+VH-barH} width={CW_C} height={barH} fill={isBreakout ? '#16C784' : 'rgba(255,255,255,0.15)'} opacity={isBreakout ? 0.9 : 0.65} rx={1.5} />;
        })}
        <text x={4} y={CH+GV+VH-2} fontSize="6" fill="rgba(255,255,255,0.25)">VOL</text>
      </svg>
      <div style={{ display: 'flex', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {[
          { label: 'Resistance',  val: '₹1,435', sub: '3 rejections',    col: '#ef4444' },
          { label: 'Breakout at', val: '₹1,437', sub: 'vol 2.1× avg',    col: '#16C784' },
          { label: 'Stop Loss',   val: '₹1,418', sub: 'base candle low', col: '#f59e0b' },
        ].map((s, i, arr) => (
          <div key={s.label} style={{ flex: 1, textAlign: 'center', padding: '7px 4px', borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
            <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', marginBottom: 2 }}>{s.label}</p>
            <p style={{ fontSize: 12, fontWeight: 900, color: s.col }}>{s.val}</p>
            <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', marginTop: 1 }}>{s.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Standard data row ────────────────────────────────────────────────────────
function ExRow({ label, val, sub, up, last }) {
  const col = up === true ? '#16C784' : up === false ? '#ef4444' : '#eceef5';
  return (
    <div className="flex items-start justify-between py-2"
      style={{ borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.05)' }}>
      <span className="text-[10px] leading-tight pt-0.5 flex-shrink-0"
        style={{ color: 'rgba(255,255,255,0.4)', maxWidth: '44%' }}>
        {label}
      </span>
      <div className="text-right flex-1 pl-2">
        <p className="text-[13px] font-bold tabular-nums leading-tight" style={{ color: col }}>{val}</p>
        {sub && <p className="text-[9px] leading-snug mt-0.5" style={{ color: 'rgba(255,255,255,0.28)' }}>{sub}</p>}
      </div>
    </div>
  );
}

// ─── Position sizing widget ───────────────────────────────────────────────────
const SIZING_DATA = {
  equity: {
    prices: [
      { label: 'Entry',     val: '₹1,437', col: '#eceef5' },
      { label: 'Stop Loss', val: '₹1,418', col: '#ef4444' },
      { label: 'Target',    val: '₹1,475', col: '#16C784' },
    ],
    riskLabel: 'Risk per share', riskVal: '₹19',
    riskFormula: '₹1,437 − ₹1,418 = ₹19',
    calcFormula: '₹500 ÷ ₹19',
    calcNote: 'Max risk 1% of ₹50,000 = ₹500',
    resultVal: '26', resultLabel: 'Shares to buy',
    pnl: [
      { label: 'Max Loss',  val: '−₹494',   sub: '26 × ₹19',  col: '#ef4444', bg: 'rgba(239,68,68,0.07)'   },
      { label: 'Max Gain',  val: '+₹988',   sub: '26 × ₹38',  col: '#16C784', bg: 'rgba(22,199,132,0.07)'  },
      { label: 'R:R Ratio', val: '1 : 2',   sub: 'min 1:1.5', col: '#6c63ff', bg: 'rgba(108,99,255,0.07)'  },
    ],
  },
  futures: {
    prices: [
      { label: 'Entry (FUT)', val: '₹1,440', col: '#eceef5' },
      { label: 'Stop Loss',   val: '₹1,418', col: '#ef4444' },
      { label: 'Target',      val: '₹1,480', col: '#16C784' },
    ],
    riskLabel: 'Risk per lot', riskVal: '₹8,800',
    riskFormula: '400 shares × ₹22 risk = ₹8,800/lot',
    calcFormula: '1 lot minimum',
    calcNote: 'Notional ₹5,76,000 · Margin ~₹1,15,200 (20%)',
    resultVal: '1', resultLabel: 'Lot to trade',
    pnl: [
      { label: 'Max Loss',  val: '−₹8,800',  sub: '400 × ₹22', col: '#ef4444', bg: 'rgba(239,68,68,0.07)'   },
      { label: 'Max Gain',  val: '+₹16,000', sub: '400 × ₹40', col: '#16C784', bg: 'rgba(22,199,132,0.07)'  },
      { label: 'R:R Ratio', val: '1 : 1.8',  sub: 'min 1:1.5', col: '#4ecca3', bg: 'rgba(78,204,163,0.07)'  },
    ],
  },
  options: {
    prices: [
      { label: 'Premium (CE)', val: '₹28', col: '#eceef5' },
      { label: 'SL — exit at', val: '₹17', col: '#ef4444' },
      { label: 'Target',       val: '₹50', col: '#16C784' },
    ],
    riskLabel: 'Risk per lot (on SL)', riskVal: '₹4,400',
    riskFormula: '400 shares × ₹11 (₹28 − ₹17) = ₹4,400',
    calcFormula: '1 lot · ₹11,200 cost',
    calcNote: 'Full premium decay = ₹11,200 max loss — size to 1 lot only',
    resultVal: '1', resultLabel: 'Lot to buy',
    pnl: [
      { label: 'Max Loss',  val: '−₹4,400',  sub: 'SL hit',      col: '#ef4444', bg: 'rgba(239,68,68,0.07)'   },
      { label: 'Max Gain',  val: '+₹8,800',  sub: '400 × ₹22',   col: '#16C784', bg: 'rgba(22,199,132,0.07)'  },
      { label: 'R:R Ratio', val: '1 : 2',    sub: 'on SL exit',  col: '#f59e0b', bg: 'rgba(245,158,11,0.07)'  },
    ],
  },
};

function SizingCalc({ instrument }) {
  const d = SIZING_DATA[instrument];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 6 }}>
        {d.prices.map(p => (
          <div key={p.label} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '8px 6px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.07)' }}>
            <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 3 }}>{p.label}</p>
            <p style={{ fontSize: 14, fontWeight: 900, color: p.col, letterSpacing: '-0.02em' }}>{p.val}</p>
          </div>
        ))}
      </div>
      <div style={{ background: 'rgba(239,68,68,0.07)', borderRadius: 10, padding: '8px 10px', border: '1px solid rgba(239,68,68,0.18)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{d.riskLabel}</p>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 16, fontWeight: 900, color: '#ef4444' }}>{d.riskVal}</p>
          <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.28)', marginTop: 1 }}>{d.riskFormula}</p>
        </div>
      </div>
      <div style={{ background: 'rgba(108,99,255,0.1)', borderRadius: 10, padding: '10px', border: '1px solid rgba(108,99,255,0.25)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', marginBottom: 2 }}>Formula</p>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontFamily: 'monospace' }}>{d.calcFormula}</p>
          <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>{d.calcNote}</p>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, paddingLeft: 12 }}>
          <p style={{ fontSize: 9, color: '#6c63ff', marginBottom: 2 }}>{d.resultLabel}</p>
          <p style={{ fontSize: 26, fontWeight: 900, color: '#6c63ff', lineHeight: 1 }}>{d.resultVal}</p>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {d.pnl.map(p => (
          <div key={p.label} style={{ flex: 1, background: p.bg, borderRadius: 10, padding: '7px 6px', textAlign: 'center', border: `1px solid ${p.col}28` }}>
            <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.38)', marginBottom: 3 }}>{p.label}</p>
            <p style={{ fontSize: 13, fontWeight: 900, color: p.col }}>{p.val}</p>
            <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.25)', marginTop: 2 }}>{p.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Order ticket ─────────────────────────────────────────────────────────────
const TICKET_DATA = {
  equity: {
    badge: 'BUY ORDER · NSE', badgeColor: '#16C784',
    symbol: 'INFY', symbolSub: 'Infosys Ltd · EQ',
    qty: '26', qtySub: 'shares',
    borderColor: 'rgba(22,199,132,0.28)',
    headerBg: 'linear-gradient(135deg, rgba(22,199,132,0.14) 0%, rgba(22,199,132,0.06) 100%)',
    headerBorder: 'rgba(22,199,132,0.15)',
    rows: [
      { label: 'Entry',     sub: 'Limit buy order',                        val: '₹1,437', col: '#eceef5' },
      { label: 'Stop Loss', sub: '−₹19/share · hard stop · set in system', val: '₹1,418', col: '#ef4444' },
      { label: 'Target',    sub: '+₹38/share · limit sell · set in system', val: '₹1,475', col: '#16C784' },
    ],
    pnl: [
      { label: 'Max Loss', val: '−₹494',   sub: '26×₹19',    col: '#ef4444', bg: 'rgba(239,68,68,0.07)'   },
      { label: 'Max Gain', val: '+₹988',   sub: '26×₹38',    col: '#16C784', bg: 'rgba(22,199,132,0.07)'  },
      { label: 'R:R',      val: '1 : 2',   sub: 'min 1:1.5', col: '#6c63ff', bg: 'rgba(108,99,255,0.07)'  },
    ],
    reasonColor: '#f59e0b',
    reasonBg: 'rgba(245,158,11,0.05)',
    reason: '"Breakout above ₹1,435 resistance (3-session high). Mild trend day. DII absorbing FII selling. Volume 2.1× average."',
    reasonCheck: '✓ Reason written — trade is valid to place',
  },
  futures: {
    badge: 'BUY FUTURES · NSE', badgeColor: '#4ecca3',
    symbol: 'INFY MAY FUT', symbolSub: 'Infosys Futures · May Expiry',
    qty: '1', qtySub: 'lot (400 sh)',
    borderColor: 'rgba(78,204,163,0.28)',
    headerBg: 'linear-gradient(135deg, rgba(78,204,163,0.14) 0%, rgba(78,204,163,0.06) 100%)',
    headerBorder: 'rgba(78,204,163,0.15)',
    rows: [
      { label: 'Entry',     sub: 'Limit buy · futures price',              val: '₹1,440', col: '#eceef5' },
      { label: 'Stop Loss', sub: '−₹22/share · ₹8,800/lot · hard stop',   val: '₹1,418', col: '#ef4444' },
      { label: 'Target',    sub: '+₹40/share · ₹16,000/lot · limit sell', val: '₹1,480', col: '#16C784' },
    ],
    pnl: [
      { label: 'Max Loss', val: '−₹8,800',  sub: '400×₹22',   col: '#ef4444', bg: 'rgba(239,68,68,0.07)'   },
      { label: 'Max Gain', val: '+₹16,000', sub: '400×₹40',   col: '#16C784', bg: 'rgba(22,199,132,0.07)'  },
      { label: 'R:R',      val: '1 : 1.8',  sub: 'min 1:1.5', col: '#4ecca3', bg: 'rgba(78,204,163,0.07)'  },
    ],
    reasonColor: '#4ecca3',
    reasonBg: 'rgba(78,204,163,0.05)',
    reason: '"Breakout above ₹1,435 resistance. Mild trend day. Futures carry — overnight NOT planned. Hard SL set at ₹1,418 immediately on entry."',
    reasonCheck: '✓ Reason written — futures trade is valid to place',
  },
  options: {
    badge: 'BUY CE · NSE', badgeColor: '#f59e0b',
    symbol: 'INFY 1440 CE', symbolSub: 'Infosys Call · May Expiry',
    qty: '1', qtySub: 'lot (400 sh)',
    borderColor: 'rgba(245,158,11,0.28)',
    headerBg: 'linear-gradient(135deg, rgba(245,158,11,0.14) 0%, rgba(245,158,11,0.06) 100%)',
    headerBorder: 'rgba(245,158,11,0.15)',
    rows: [
      { label: 'Entry',     sub: 'Buy at market · ATM call',              val: '₹28',    col: '#eceef5' },
      { label: 'SL rule',   sub: 'Exit if premium ≤ ₹17 · 40% decay',    val: '₹17',    col: '#ef4444' },
      { label: 'Target',    sub: '+₹22/share · limit sell order set',     val: '₹50',    col: '#16C784' },
    ],
    pnl: [
      { label: 'Max Loss',  val: '−₹4,400',  sub: 'SL hit',     col: '#ef4444', bg: 'rgba(239,68,68,0.07)'  },
      { label: 'Max Gain',  val: '+₹8,800',  sub: '400×₹22',    col: '#16C784', bg: 'rgba(22,199,132,0.07)' },
      { label: 'R:R',       val: '1 : 2',    sub: 'on SL exit', col: '#f59e0b', bg: 'rgba(245,158,11,0.07)' },
    ],
    reasonColor: '#f59e0b',
    reasonBg: 'rgba(245,158,11,0.05)',
    reason: '"ATM CE on breakout above ₹1,435. Mild trend day. Volume 2.1× avg. Do not hold past 2:30 PM — time decay kills premium."',
    reasonCheck: '✓ Reason written — options trade is valid to place',
  },
};

function OrderTicket({ instrument }) {
  const d = TICKET_DATA[instrument];
  return (
    <div style={{ borderRadius: 14, overflow: 'hidden', border: `1.5px solid ${d.borderColor}`, background: 'rgba(0,0,0,0.25)' }}>
      <div style={{ background: d.headerBg, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: `1px solid ${d.headerBorder}` }}>
        <div>
          <p style={{ fontSize: 9, fontWeight: 700, color: d.badgeColor, letterSpacing: '0.06em', marginBottom: 2 }}>{d.badge}</p>
          <p style={{ fontSize: 20, fontWeight: 900, color: '#eceef5', letterSpacing: '-0.02em', lineHeight: 1 }}>{d.symbol}</p>
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>{d.symbolSub}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 2 }}>QUANTITY</p>
          <p style={{ fontSize: 26, fontWeight: 900, color: '#eceef5', lineHeight: 1 }}>{d.qty}</p>
          <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{d.qtySub}</p>
        </div>
      </div>
      <div style={{ padding: '4px 14px' }}>
        {d.rows.map((r, i, arr) => (
          <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', padding: '9px 0' }}>
            <div>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 2 }}>{r.label}</p>
              <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)', lineHeight: 1.3 }}>{r.sub}</p>
            </div>
            <p style={{ fontSize: 16, fontWeight: 900, color: r.col }}>{r.val}</p>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {d.pnl.map((p, i, arr) => (
          <div key={p.label} style={{ flex: 1, background: p.bg, padding: '8px 6px', textAlign: 'center', borderRight: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
            <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', marginBottom: 3 }}>{p.label}</p>
            <p style={{ fontSize: 13, fontWeight: 900, color: p.col }}>{p.val}</p>
            <p style={{ fontSize: 8, color: 'rgba(255,255,255,0.22)', marginTop: 2 }}>{p.sub}</p>
          </div>
        ))}
      </div>
      <div style={{ padding: '10px 14px', background: d.reasonBg }}>
        <p style={{ fontSize: 8, fontWeight: 900, color: d.reasonColor, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 6 }}>
          Written Reason (mandatory)
        </p>
        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, fontStyle: 'italic' }}>
          {d.reason}
        </p>
        <p style={{ fontSize: 9, color: '#16C784', marginTop: 6, fontWeight: 700 }}>
          {d.reasonCheck}
        </p>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function TradePlanCards({ navigate }) {
  const [openIdx,     setOpenIdx]     = useState(0);
  const [instrumentId, setInstrument] = useState('equity');

  return (
    <div style={{ padding: '0 16px' }}>

      {/* Header */}
      <div style={{ paddingBottom: 14 }}>
        <p className="text-[11px] font-black uppercase tracking-widest mb-1.5" style={{ color: '#f59e0b' }}>
          Trade Plan Walkthrough
        </p>
        <p className="text-[17px] font-black leading-tight" style={{ color: '#eceef5', letterSpacing: '-0.02em' }}>
          How to plan a trade — step by step
        </p>
        <p className="text-[12px] mt-1 leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
          A complete example using INFY. Real numbers, every step.
        </p>
      </div>

      {/* Instrument tabs */}
      <div data-tour="trade-plan-instrument-tabs" style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {INSTRUMENTS.map(inst => {
          const active = instrumentId === inst.id;
          return (
            <button
              key={inst.id}
              onClick={() => { setInstrument(inst.id); setOpenIdx(0); }}
              style={{
                flex: 1, padding: '7px 4px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: active ? `${inst.color}20` : 'rgba(255,255,255,0.04)',
                border: `1px solid ${active ? inst.color + '50' : 'rgba(255,255,255,0.07)'}`,
                color: active ? inst.color : 'rgba(255,255,255,0.35)',
                fontSize: 11, fontWeight: active ? 800 : 500,
                transition: 'all 0.18s ease',
              }}
            >
              {inst.label}
            </button>
          );
        })}
      </div>

      {/* Step list */}
      <div data-tour="trade-plan-steps" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {STEPS.map((step, idx) => {
          const Icon     = step.icon;
          const isOpen   = openIdx === idx;
          const stepNo   = String(idx + 1).padStart(2, '0');
          const color    = step.colorByInstrument ? step.colorByInstrument[instrumentId] : step.color;
          const verdict  = step.verdictByInstrument ? step.verdictByInstrument[instrumentId] : step.verdict;
          const vColor   = step.verdictColorByInstrument ? step.verdictColorByInstrument[instrumentId] : step.verdictColor;

          return (
            <div key={idx} data-tour={`trade-plan-step-${idx + 1}`} style={{
              borderRadius: 16,
              border: `1px solid ${isOpen ? color + '40' : 'rgba(255,255,255,0.07)'}`,
              background: isOpen
                ? 'linear-gradient(160deg, #0d0f1e 0%, #07080f 100%)'
                : 'rgba(255,255,255,0.02)',
              overflow: 'hidden',
              transition: 'border-color 0.2s ease, background 0.2s ease',
            }}>

              {isOpen && (
                <div style={{ height: 2, background: `linear-gradient(90deg, ${color}, ${color}44)` }} />
              )}

              {/* Header row */}
              <button
                onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                style={{
                  width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                  padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
                  textAlign: 'left',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 12, flexShrink: 0,
                  background: isOpen ? `${color}18` : 'rgba(255,255,255,0.04)',
                  border: `1.5px solid ${isOpen ? color + '38' : 'rgba(255,255,255,0.07)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}>
                  <Icon size={16} color={isOpen ? color : 'rgba(255,255,255,0.3)'} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: '0.05em', color: isOpen ? color : 'rgba(255,255,255,0.22)' }}>
                      STEP {stepNo}
                    </span>
                    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.18)' }}>·</span>
                    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.22)' }}>{step.zone}</span>
                  </div>
                  <p style={{
                    fontSize: 14, fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.01em',
                    color: isOpen ? '#eceef5' : 'rgba(255,255,255,0.55)',
                    transition: 'color 0.2s ease',
                  }}>
                    {step.title}
                  </p>
                </div>

                <div style={{
                  flexShrink: 0, width: 26, height: 26, borderRadius: 8,
                  background: isOpen ? `${color}15` : 'rgba(255,255,255,0.04)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}>
                  <ChevronDown
                    size={14}
                    color={isOpen ? color : 'rgba(255,255,255,0.22)'}
                    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.22s ease' }}
                  />
                </div>
              </button>

              {/* Expanded body */}
              {isOpen && (
                <div style={{ padding: '0 14px 16px' }}>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.48)', lineHeight: 1.65, marginBottom: 14 }}>
                    {step.desc}
                  </p>

                  <p style={{ fontSize: 8, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', color: `${color}70`, marginBottom: 8 }}>
                    Example data
                  </p>

                  <div style={{ marginBottom: 14 }}>
                    {step.type === 'rows' && (
                      <div style={{ borderRadius: 12, background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.06)', padding: '0 10px' }}>
                        {step.rows.map((row, i) => (
                          <ExRow key={i} {...row} last={i === step.rows.length - 1} />
                        ))}
                        <div style={{ height: 4 }} />
                      </div>
                    )}
                    {step.type === 'chart'  && <BreakoutChart />}
                    {step.type === 'sizing' && <SizingCalc instrument={instrumentId} />}
                    {step.type === 'ticket' && <OrderTicket instrument={instrumentId} />}
                  </div>

                  <div style={{
                    borderRadius: 12, padding: '10px 12px', marginBottom: 12,
                    background: `${vColor}0e`, border: `1px solid ${vColor}25`,
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                  }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: vColor, marginTop: 5, flexShrink: 0 }} />
                    <p style={{ fontSize: 12, color: vColor, fontWeight: 600, lineHeight: 1.45 }}>{verdict}</p>
                  </div>

                  <button
                    onClick={() => navigate(step.path)}
                    style={{
                      width: '100%', padding: '10px', borderRadius: 12,
                      background: `${color}12`, color: color,
                      border: `1px solid ${color}30`,
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Open in app →
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
